import dataProviderFactory from "../dataProvider";
import { GET_ONE, GET_LIST, UPDATE } from "react-admin";
import { path } from "ramda";

class TourDataService {
  /**
   * Obtiene los datos de un tour pendiente
   * @param {string} tourId - ID del tour
   * @returns {Promise<Object>} Datos del tour
   */
  static async fetchPendingTourData(tourId) {
    try {
      console.log('Fetching pending tour data for ID:', tourId)
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_ONE, "tour_management", { id: tourId });
      console.log('Pending tour data response:', response)
      return response.data;
    } catch (error) {
      console.error("Error fetching pending tour data:", error);
      throw error;
    }
  }

  /**
   * Obtiene los datos de un tour confirmado para edición
   * @param {string} tourId - ID del tour
   * @returns {Promise<Object>} Datos del tour
   */
  static async fetchConfirmedTourData(tourId) {
    try {
      console.log('Fetching confirmed tour data for ID:', tourId)
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_ONE, "tour_clients", { id: tourId });
      console.log('Confirmed tour data response:', response)
      return response.data;
    } catch (error) {
      console.error("Error fetching confirmed tour data:", error);
      throw error;
    }
  }

  /**
   * Obtiene los modelos de vehículos disponibles
   * @param {string} type - Tipo de vehículo (2 para tours, 2_2 para edición)
   * @returns {Promise<Array>} Lista de modelos
   */
  static async fetchVehicleModels(type = "2") {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "vehicle_models", {
        filter: { active: true, type },
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "order", order: "ASC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicle models:", error);
      throw error;
    }
  }

  /**
   * Obtiene los vehículos disponibles para un tour
   * @param {string} tourId - ID del tour
   * @param {number} vehicleModelId - ID del modelo de vehículo (opcional)
   * @returns {Promise<Array>} Lista de vehículos
   */
  static async fetchAvailableVehicles(tourId, vehicleModelId) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      
      // Construir el filtro dinámicamente
      const filter = {
        availability: true,
        tour_id: tourId
      };
      
      // Solo agregar vehicle_model_id si se proporciona
      if (vehicleModelId) {
        filter.vehicle_model_id = vehicleModelId;
      }
      
      const response = await dataProvider(GET_LIST, "vehicles", {
        filter,
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "order", order: "ASC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching available vehicles:", error);
      throw error;
    }
  }

  /**
   * Obtiene los dispositivos GPS disponibles
   * @param {string} tourId - ID del tour
   * @returns {Promise<Array>} Lista de dispositivos GPS
   */
  static async fetchGpsDevices(tourId) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "gps_devices", {
        filter: {
          tour_id: tourId,
          availability: true
        },
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "order", order: "ASC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching GPS devices:", error);
      throw error;
    }
  }

  /**
   * Obtiene los precios para un tour específico
   * @param {string} tourId - ID del tour
   * @param {string} priceDate - Fecha de precios
   * @returns {Promise<Array>} Lista de precios
   */
  static async fetchTourPrices(tourId, priceDate) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "prices", {
        filter: {
          for_date: priceDate,
          for_tour_id: tourId
        },
        pagination: { page: 1, perPage: 99999 },
        sort: {}
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching tour prices:", error);
      throw error;
    }
  }

  /**
   * Obtiene los precios de GPS para una fecha específica
   * @param {string} priceDate - Fecha de precios
   * @returns {Promise<Array>} Lista de precios de GPS
   */
  static async fetchGpsPrices(priceDate) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "prices", {
        filter: {
          price_item_type: "gps_device",
          for_date: priceDate
        },
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "start_date", order: "DESC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching GPS prices:", error);
      throw error;
    }
  }

  /**
   * Obtiene los precios de seguros para una fecha específica
   * @param {string} priceDate - Fecha de precios
   * @returns {Promise<Array>} Lista de precios de seguros
   */
  static async fetchInsurancePrices(priceDate) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "prices", {
        filter: {
          price_item_type: "insurance",
          for_date: priceDate
        },
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "start_date", order: "DESC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching insurance prices:", error);
      throw error;
    }
  }

  /**
   * Obtiene las recompensas disponibles de un cliente
   * @param {string} clientId - ID del cliente
   * @returns {Promise<number>} Cantidad de recompensas disponibles
   */
  static async fetchClientRewards(clientId) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_ONE, "clients", { id: clientId });
      return path(["data", "availableRewards"], response) || 0;
    } catch (error) {
      console.error("Error fetching client rewards:", error);
      return 0;
    }
  }

  /**
   * Obtiene las ubicaciones disponibles
   * @returns {Promise<Array>} Lista de ubicaciones
   */
  static async fetchLocations() {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "locations", {
        filter: { only_bases: "true" },
        pagination: { page: 1, perPage: 999 },
        sort: { field: "created_at", order: "DESC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  }

  /**
   * Obtiene los días festivos para una ubicación y rango de fechas
   * @param {string} locationId - ID de la ubicación
   * @param {string} startDate - Fecha de inicio
   * @param {string} endDate - Fecha de fin
   * @returns {Promise<Array>} Lista de días festivos
   */
  static async fetchHolidays(locationId, startDate, endDate) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      const response = await dataProvider(GET_LIST, "holidays", {
        filter: {
          startDate,
          endDate,
          location_id: locationId
        },
        pagination: { page: 1, perPage: 99999 },
        sort: { field: "created_at", order: "DESC" }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching holidays:", error);
      return [];
    }
  }

  /**
   * Actualiza un tour pendiente
   * @param {Object} submitData - Datos completos del formulario
   * @returns {Promise<Object>} Respuesta de la actualización
   */
  static async updatePendingTour(submitData) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      
      // Preparar los datos para el backend
      const updateData = {
        vehicle_model_1: submitData.vehicle.modelId,
        vehicle_id: submitData.vehicle.vehicleId,
        gpsdevice_id: submitData.vehicle.gpsDeviceId,
        r_gps: submitData.options.gps,
        r_insurance: submitData.options.insurance,
        r_cancel_insurance: submitData.options.cancelInsurance,
        r_single: submitData.options.single,
        low_seat: submitData.options.lowSeat,
        value_to_charge: submitData.pricing.total, // Usar el valor total del tour
        partial: submitData.payment.partial,
        charge_type_id: submitData.payment.chargeType,
        discount: submitData.discounts.discount,
        rewards: submitData.discounts.rewards,
        imt_comment: submitData.comments.imtComment,
        extra_comments: submitData.comments.clientComment,
        noConfirmationEmail: submitData.settings.noConfirmationEmail,
        checkedF: submitData.settings.confirmValue,
        invoice_comments: submitData.comments.imtComment || "Procesamiento de tour"
      };

      const bodyRequest = {
        id: submitData.tourId,
        data: updateData
      };

      const result = await dataProvider(UPDATE, "tour_management", bodyRequest);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error("Error updating pending tour:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Actualiza un tour confirmado
   * @param {Object} submitData - Datos completos del formulario
   * @returns {Promise<Object>} Respuesta de la actualización
   */
  static async updateConfirmedTour(submitData) {
    try {
      const dataProvider = await dataProviderFactory("rest");
      
      // Preparar los datos para el backend
      const updateData = {
        vehicle_model_1: submitData.vehicle.modelId,
        vehicle_id: submitData.vehicle.vehicleId,
        gpsdevice_id: submitData.vehicle.gpsDeviceId,
        r_gps: submitData.options.gps,
        r_insurance: submitData.options.insurance,
        r_cancel_insurance: submitData.options.cancelInsurance,
        r_single: submitData.options.single,
        low_seat: submitData.options.lowSeat,
        value_to_charge: submitData.pricing.total, // Usar el valor total del tour
        partial: submitData.payment.partial,
        charge_type_id: submitData.payment.chargeType,
        discount: submitData.discounts.discount,
        rewards: submitData.discounts.rewards,
        imt_comment: submitData.comments.imtComment,
        extra_comments: submitData.comments.clientComment,
        noConfirmationEmail: submitData.settings.noConfirmationEmail,
        checkedF: submitData.settings.confirmValue,
        invoice_comments: submitData.comments.imtComment || "Procesamiento de tour"
      };

      const bodyRequest = {
        id: submitData.tourId,
        data: updateData
      };

      const result = await dataProvider(UPDATE, "tour_clients", bodyRequest);
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error("Error updating confirmed tour:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Obtiene el tipo de Stripe basado en el tour
   * @param {Object} rental - Datos del rental
   * @returns {string|null} Tipo de Stripe
   */
  static getStripeType(rental) {
    if (rental.booking.tour_id && rental.booking.tour_id > 0) {
      return rental.booking.p4m ? "p4m" : "tours";
    }
    return null;
  }

  /**
   * Verifica si el usuario tiene Stripe configurado
   * @param {Object} rental - Datos del rental
   * @returns {boolean} True si tiene Stripe configurado
   */
  static isUserStripe(rental) {
    if (!rental) return false;
    
    const stripeType = this.getStripeType(rental);
    if (stripeType !== "p4m") {
      const stripeKey = `stripe_${stripeType}`;
      return rental.driver && rental.driver[stripeKey];
    }
    return false;
  }

  /**
   * Mapea los datos del tour desde TourDetailsDialog a la estructura esperada por TourProcessingForm
   * @param {Object} tourData - Datos del tour desde TourDetailsDialog
   * @returns {Object} Datos mapeados para el modal de procesamiento
   */
  static mapTourDataToProcessingFormat(tourData) {
    console.log('Mapping tour data:', tourData);
    
    if (!tourData) {
      console.error('No tour data provided for mapping');
      return null;
    }

    const mappedData = {
      driver: {
        id: (tourData.booking_info && tourData.booking_info.r_id) || (tourData.booking_info && tourData.booking_info.id),
        first_name: (tourData.booking_info && tourData.booking_info.r_first_name) || (tourData.booking_info && tourData.booking_info.first_name),
        last_name: (tourData.booking_info && tourData.booking_info.r_last_name) || (tourData.booking_info && tourData.booking_info.last_name),
        email: (tourData.booking_info && tourData.booking_info.r_email) || (tourData.booking_info && tourData.booking_info.email),
        phone: (tourData.booking_info && tourData.booking_info.r_home_phone) || (tourData.booking_info && tourData.booking_info.phone)
      },
      booking: {
        id: tourData.rental_info && tourData.rental_info.id,
        tour_id: (tourData.rental_info && tourData.rental_info.tour_id) || (tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.id),
        vehicle_model_1: tourData.rental_info && tourData.rental_info.vehicle_model_1,
        vehicle_model_2: tourData.rental_info && tourData.rental_info.vehicle_model_2,
        tour_start_date: tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.tour_start_date,
        tour_end_date: tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.tour_end_date,
        price: tourData.rental_info && tourData.rental_info.calculated_pricing && tourData.rental_info.calculated_pricing.total_price,
        price_date: tourData.rental_info && tourData.rental_info.price_date,
        r_gps: (tourData.rental_info && tourData.rental_info.r_gps) || false,
        r_insurance: (tourData.rental_info && tourData.rental_info.r_insurance) || false,
        r_cancel_insurance: (tourData.rental_info && tourData.rental_info.r_cancel_insurance) || false,
        r_single: (tourData.rental_info && tourData.rental_info.r_single) || false,
        rewards: (tourData.rental_info && tourData.rental_info.rewards) || 0,
        has_passenger: (tourData.booking_info && tourData.booking_info.p_first_name) ? true : false
      },
      tour: {
        id: tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.id,
        name: tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.tour_name,
        effective_start_date: tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.effective_start_date,
        effective_end_date: tourData.booking_info && tourData.booking_info.tour_info && tourData.booking_info.tour_info.effective_end_date
      },
      location_id_1: tourData.rental_info && tourData.rental_info.location_id_1,
      location_id_2: tourData.rental_info && tourData.rental_info.location_id_2,
      date_1: tourData.rental_info && tourData.rental_info.date_1,
      date_2: tourData.rental_info && tourData.rental_info.date_2,
      time_1: tourData.rental_info && tourData.rental_info.time_1,
      time_2: tourData.rental_info && tourData.rental_info.time_2,
      status: tourData.rental_info && tourData.rental_info.status
    };

    console.log('Mapped tour data:', mappedData);
    return mappedData;
  }
}

export default TourDataService; 


// WEBPACK FOOTER //
// ./src/services/tourDataService.js